"use client";

/**
 * Interactive guide toolkit (Phase 7): an evaluation checklist and a sample SOP,
 * each with WORKING copy / print / CSV / PDF actions — no email gate, no fake
 * buttons. PDF uses pdf-lib (already a dependency), lazy-imported only on click
 * so it never weighs down the initial bundle. Print uses a hidden iframe so it
 * prints just the resource (not the whole page) and survives popup blockers.
 *
 * Analytics fire on each action (resource_type only — never checklist contents).
 */
import * as React from "react";
import posthog from "posthog-js";
import type { AnalyticsEvent } from "@/lib/analytics/events";

function capture(event: AnalyticsEvent, resourceType: string) {
  try {
    posthog.capture(event, { resource_type: resourceType, cta_location: "guide_toolkit" });
  } catch {
    /* analytics must never break the page */
  }
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* clipboard may be blocked; silent */
  }
}

function printHtml(title: string, bodyHtml: string) {
  const iframe = document.createElement("iframe");
  Object.assign(iframe.style, {
    position: "fixed",
    right: "0",
    bottom: "0",
    width: "0",
    height: "0",
    border: "0",
  });
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow?.document;
  if (!doc) return;
  doc.open();
  doc.write(
    `<!doctype html><html><head><title>${title}</title><style>` +
      `body{font:13px -apple-system,system-ui,sans-serif;padding:28px;color:#111}` +
      `h1{font-size:18px;margin:0 0 12px}ul{padding-left:18px}li{margin:5px 0}` +
      `table{border-collapse:collapse;width:100%;margin-top:8px}` +
      `td,th{border:1px solid #ccc;padding:6px 8px;text-align:left;vertical-align:top;font-size:12px}` +
      `.note{color:#555;font-size:11px;margin-top:12px}` +
      `</style></head><body>${bodyHtml}</body></html>`,
  );
  doc.close();
  iframe.contentWindow?.focus();
  iframe.contentWindow?.print();
  window.setTimeout(() => iframe.remove(), 1000);
}

async function downloadPdf(filename: string, title: string, lines: string[]) {
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const W = 595,
    H = 842,
    margin = 50,
    maxW = W - margin * 2;
  let page = doc.addPage([W, H]);
  let y = H - margin;
  const draw = (text: string, size: number, f: typeof font) => {
    const lineH = size * 1.45;
    const words = text.split(" ");
    let line = "";
    const flush = () => {
      if (!line) return;
      if (y < margin + lineH) {
        page = doc.addPage([W, H]);
        y = H - margin;
      }
      page.drawText(line, { x: margin, y, size, font: f, color: rgb(0.1, 0.1, 0.1) });
      y -= lineH;
      line = "";
    };
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (f.widthOfTextAtSize(test, size) > maxW) {
        flush();
        line = w;
      } else {
        line = test;
      }
    }
    flush();
  };
  draw(title, 16, bold);
  y -= 6;
  for (const l of lines) draw(l, 11, l.trim() === "" ? font : font);
  draw("", 11, font);
  draw("Generated from pcnshark.com — adapt to your own process.", 9, font);
  const bytes = await doc.save();
  downloadBlob(filename, new Blob([bytes as BlobPart], { type: "application/pdf" }));
}

// ─── Evaluation checklist ───────────────────────────────────────────────────

export function EvaluationChecklist({
  heading,
  intro,
  items,
}: {
  heading: string;
  intro?: string;
  items: string[];
}) {
  const [checked, setChecked] = React.useState<boolean[]>(() => items.map(() => false));
  const toggle = (i: number) =>
    setChecked((prev) => prev.map((v, j) => (j === i ? !v : v)));
  const reset = () => setChecked(items.map(() => false));
  // Filename derived from the heading so downloads read correctly on any guide
  // that reuses this component (PCN evaluation checklist, EOL/LTB checklist, …).
  const fileBase =
    heading.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48) ||
    "checklist";

  const asText = () =>
    `${heading}\n\n` +
    items.map((it, i) => `[${checked[i] ? "x" : " "}] ${it}`).join("\n") +
    `\n\nGenerated from pcnshark.com`;
  const asCsv = () =>
    "Item,Checked\n" +
    items.map((it, i) => `"${it.replace(/"/g, '""')}",${checked[i] ? "yes" : "no"}`).join("\n");
  const asHtml = () =>
    `<h1>${heading}</h1><ul>` +
    items.map((it, i) => `<li>${checked[i] ? "☑" : "☐"} ${it}</li>`).join("") +
    `</ul><p class="note">Generated from pcnshark.com</p>`;

  return (
    <section id="checklist" className="scroll-mt-20 mt-12">
      <h2 className="font-display font-semibold text-[22px] lg:text-[26px] tracking-[-0.02em]">
        {heading}
      </h2>
      {intro && (
        <p className="mt-3 text-[15px] ink-2 leading-relaxed max-w-[70ch]">{intro}</p>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        <ToolbarButton onClick={() => { copyText(asText()); capture("guide_checklist_copy", "checklist"); }}>
          Copy checklist
        </ToolbarButton>
        <ToolbarButton onClick={() => { printHtml(heading, asHtml()); capture("guide_checklist_print", "checklist"); }}>
          Print checklist
        </ToolbarButton>
        <ToolbarButton onClick={() => { downloadBlob(`${fileBase}.csv`, new Blob([asCsv()], { type: "text/csv" })); capture("guide_checklist_csv_download", "checklist"); }}>
          Download CSV
        </ToolbarButton>
        <ToolbarButton onClick={() => { downloadPdf(`${fileBase}.pdf`, heading, items.map((it, i) => `[${checked[i] ? "x" : " "}] ${it}`)); capture("guide_checklist_pdf_download", "checklist"); }}>
          Download PDF
        </ToolbarButton>
        <ToolbarButton onClick={reset} variant="ghost">
          Reset
        </ToolbarButton>
      </div>

      <ul className="mt-5 border rule bg-white">
        {items.map((it, i) => (
          <li key={i} className="border-b rule last:border-b-0">
            <label className="flex items-start gap-3 px-5 py-3.5 cursor-pointer text-[14px] ink-2 leading-relaxed hover:bg-paper-2 transition-colors">
              <input
                type="checkbox"
                checked={checked[i]}
                onChange={() => toggle(i)}
                className="mt-0.5 accent-[color:var(--ink)] h-4 w-4 shrink-0"
              />
              <span>{it}</span>
            </label>
          </li>
        ))}
      </ul>
      <p className="mt-2 font-mono text-[11px] ink-3">
        Your selections stay in your browser — nothing is uploaded, and no email
        is required to copy, print, or download.
      </p>
    </section>
  );
}

// ─── Sample SOP ─────────────────────────────────────────────────────────────

export function SopTable({
  heading,
  intro,
  disclaimer,
  rows,
}: {
  heading: string;
  intro?: string;
  disclaimer?: string;
  rows: { stage: string; owner: string; output: string }[];
}) {
  const asText = () =>
    `${heading}\n\n` +
    rows.map((r) => `${r.stage}\t${r.owner}\t${r.output}`).join("\n") +
    (disclaimer ? `\n\n${disclaimer}` : "") +
    `\n\nGenerated from pcnshark.com`;
  const asHtml = () =>
    `<h1>${heading}</h1><table><thead><tr><th>Stage</th><th>Suggested owner</th><th>Required output</th></tr></thead><tbody>` +
    rows.map((r) => `<tr><td>${r.stage}</td><td>${r.owner}</td><td>${r.output}</td></tr>`).join("") +
    `</tbody></table>` +
    (disclaimer ? `<p class="note">${disclaimer}</p>` : "");
  const pdfLines = [
    "Stage / Suggested owner / Required output",
    "",
    ...rows.map((r) => `${r.stage} — ${r.owner} — ${r.output}`),
    ...(disclaimer ? ["", disclaimer] : []),
  ];

  return (
    <section id="sop" className="scroll-mt-20 mt-12">
      <h2 className="font-display font-semibold text-[22px] lg:text-[26px] tracking-[-0.02em]">
        {heading}
      </h2>
      {intro && (
        <p className="mt-3 text-[15px] ink-2 leading-relaxed max-w-[70ch]">{intro}</p>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        <ToolbarButton onClick={() => { copyText(asText()); capture("guide_sop_copy", "sop"); }}>
          Copy SOP
        </ToolbarButton>
        <ToolbarButton onClick={() => { printHtml(heading, asHtml()); capture("guide_sop_print", "sop"); }}>
          Print SOP
        </ToolbarButton>
        <ToolbarButton onClick={() => { downloadPdf("pcn-management-sop.pdf", heading, pdfLines); capture("guide_sop_pdf_download", "sop"); }}>
          Download the PCN response SOP
        </ToolbarButton>
      </div>

      <div className="mt-5 border rule bg-white overflow-x-auto">
        <table className="w-full border-collapse text-left min-w-[560px]">
          <thead>
            <tr className="border-b rule bg-paper-2">
              {["Stage", "Suggested owner", "Required output"].map((h) => (
                <th key={h} className="px-5 py-3 font-mono text-[10px] uppercase tracking-wider ink-3 font-normal">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.stage} className="border-b rule last:border-b-0 align-top">
                <th scope="row" className="px-5 py-3.5 text-left font-display font-semibold text-[13.5px]">
                  {r.stage}
                </th>
                <td className="px-5 py-3.5 text-[13px] ink-2">{r.owner}</td>
                <td className="px-5 py-3.5 text-[13px] ink-2">{r.output}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {disclaimer && (
        <p className="mt-3 font-mono text-[11px] ink-3 leading-relaxed max-w-[80ch]">
          {disclaimer}
        </p>
      )}
    </section>
  );
}

function ToolbarButton({
  onClick,
  children,
  variant = "solid",
}: {
  onClick: () => void;
  children: React.ReactNode;
  variant?: "solid" | "ghost";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center justify-center px-3.5 py-2 text-[12.5px] font-medium transition " +
        (variant === "ghost"
          ? "border rule ink-2 hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]"
          : "bg-[color:var(--ink)] text-white hover:bg-black")
      }
    >
      {children}
    </button>
  );
}
